import { ApplicationRef, Injectable, Injector, Type, createComponent, Renderer2, RendererFactory2, inject, ElementRef, DOCUMENT, signal, computed, ComponentRef } from '@angular/core';
import { NotificationComponent } from '../components/notification/notification.component';
import { Branch, MessageServerAccount } from '../interfaces';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../auth';
import { notificationTarget } from './notification-link';

export interface Notification {
	key?: string;
	summary: string;
	detail?: string;
	icon?: string;
	severity?: 'info' | 'success' | 'warn' | 'error';
	sticky?: boolean;
	lifetime?: number;
	data?: Object;
	onClick?: () => void;
}

/**
 * What the backend actually puts on the wire: `NotificationService.emit` defaults to `'info'` and its
 * wrappers send `'warning'` / `'error'`. `'success'` and `'warn'` are the toast vocabulary and are
 * tolerated here so a hand-rolled emit does not fail the type; `toastSeverity` reconciles the two.
 */
export type ServerSeverity = 'info' | 'success' | 'warn' | 'warning' | 'error';

export type ServerNotification = {
    /** The `notifications` row id - a number, as `Notification.to_dict()` sends it. */
    id: number;
    severity: ServerSeverity;
    type: string;
    data: any;
} & (
    | {type: 'text', data: {title: string, body: string}}
    | {type: 'office365_connected', data: {user: string}}
    | {type: 'new_message', data: {ticketId: string, snowflake: string}}
    | {type: 'message_server_account_reassigned', data: {branch: Pick<Branch, "id" | "name">, message_server_account: Pick<MessageServerAccount, "id" | "name">}}
    /**
     * The mail worker gave up on a mail account after repeated connect/login failures and stopped
     * polling it. Sent once per outage, not once per attempt - see
     * `backend/services/mail_account_health.py`.
     */
    | {type: 'mail_account_failing', data: {mailAccountId: number, user: string, imapServer: string | null, stage: string, reason: MailAccountFailureReason, detail: string | null}}
    /** The same account connected again; the worker is back to its normal interval. */
    | {type: 'mail_account_recovered', data: {mailAccountId: number, user: string}}
    /**
     * One mail kept failing to process while the account itself works; it stays unread in the
     * mailbox and the mails behind it go ahead. Sent once per mail - see
     * `backend/services/mail_message_failures.py`.
     */
    | {type: 'mail_processing_failed', data: {mailAccountId: number, user: string, sender: string | null, subject: string | null, attempts: number, detail: string}}
    /** `routers/user.py` - ids and counts only, never names or addresses. */
    | {type: 'users_merged', data: {userId: number, sourceUserId: number, counts: Record<string, number>}}
    | {type: 'user_anonymized', data: {userId: number, counts: Record<string, number>}}
    | {type: 'user_hard_deleted', data: {userId: number, counts: Record<string, number>}}
    /** `services/user_import_worker.py`. */
    | {type: 'user_import_completed', data: {jobId: number, filename: string, total: number, created: number, duplicates: number, inFileDuplicates: number, failed: number}}
    | {type: 'user_import_failed', data: {jobId: number, error: string}}
    /** `services/newsletter_service.py` - counts only, never recipient names. */
    | {type: 'newsletter_queued', data: {newsletterId: number, name: string, template: string, createdBy: string | null, total: number, whatsapp: number, email: number, unreachable: number, startAt: string | null}}
    | {type: 'newsletter_completed', data: {newsletterId: number, name: string | null, sent: number, failed: number, skipped: number, canceled: number}}
    /** `services/db.py` - `template` is the provider payload, passed through as it arrived. */
    | {type: 'template_status_update', data: {account_id: number, branch_id: number | string, template: {id?: string, name?: string, status?: string}}}
    /**
     * `services/db.py` - Meta re-rated a template's quality on one account. Emitted for every
     * callback, `warning` only when it drops to RED; the templates page patches its matrix from it.
     */
    | {type: 'template_quality_update', data: {account_id: number, branch_id: number | string, name?: string, template: {id?: string, quality_rating?: string}}}
    /**
     * `services/db.py` - Meta took a template out of service on some account *and* enabled
     * automations send it. Separate from `template_status_update`, which says "Meta changed its
     * mind" and points at the templates page; this says "something you switched on will now fail"
     * and points at the automations. Only emitted when at least one flow is affected.
     */
    | {type: 'whatsapp_template_unusable', data: {templateId: number, name?: string, language?: string, accountId: number, branchId: number | string, reviewStatus: string, count: number, automations: Array<{id: number, name: string, trigger: string}>}}
    /** `routers/branch.py` - callbacks could not be registered, so no status will ever arrive. */
    | {type: 'whatsapp_webhook_registration_failed', data: {accountId: number, accountName: string, branchId: number | string, reason: string}}
    /** `routers/integrations.py` / `routers/branch.py` - the server-managed templates (consent, newsletter) could not be created on a linked account. */
    | {type: 'managed_template_provision_failed', data: {accountId: number, accountName: string, branchId?: number | string, families: string[], reason: string}}
    /** `services/whatsapp_template_worker.py` - one per template once it stops moving, listing the accounts that failed. */
    | {type: 'whatsapp_template_deploy_failed', data: {templateId: number, name?: string, language?: string, failed: Array<{accountId: number, errorCode: string | null}>}}
    /** Emitted by the `notification` automation node; both ids are null when the run had no context. */
    | {type: 'automation_message', data: {message: string, ticketId: number | null, userId: number | null}}
    /** `services/automation_worker.py` - sends piling up past the spillover threshold. */
    | {type: 'automation_send_backlog', data: {deferred: number, threshold: number}}
)

/**
 * Stable codes from `backend/services/mail_account_health.py`. The backend also sends an untranslated
 * `detail`, which is what we fall back to when it starts sending a code this client predates.
 */
/**
 * Reconcile the two severity vocabularies: the backend's `warning` is the toast's `warn`. They have
 * disagreed since the table was added, which is why `notification-center.ts` matches both spellings.
 */
export function toastSeverity(severity: ServerSeverity | undefined): Notification['severity'] {
	return severity === 'warning' ? 'warn' : (severity ?? 'info');
}

export type MailAccountFailureReason =
	| 'missing_configuration'
	| 'invalid_credentials'
	| 'token_refresh_failed'
	| 'host_unreachable'
	| 'tls_error'
	| 'timeout'
	| 'mailbox_error'
	| 'unknown';

@Injectable({ providedIn: 'root' })
export class NotificationService {
	public  static _notifications = signal<ServerNotification[]>([]);

	public notifications = computed(() => NotificationService._notifications());
	public notificationCount = computed(() => NotificationService._notifications().length);

	private static readonly notificationDict = new Map<string, ComponentRef<any>[]>();

    private static CONTAINER: ElementRef<HTMLElement> | null = null;
	private readonly renderer: Renderer2;
    private readonly appRef = inject(ApplicationRef);
    private readonly injector = inject(Injector);
    private readonly document = inject(DOCUMENT);
    private router = inject(Router);
    private readonly translate = inject(TranslateService);
    /**
     * Only used to decide whether a link may be offered. Safe to inject here: `auth.ts` imports
     * nothing from this app, so this cannot close a cycle back through `services/http.ts`.
     */
    private readonly auth = inject(AuthService);

	constructor(readonly rendererFactory: RendererFactory2) {
		this.renderer = rendererFactory.createRenderer(null, null);
		this.fetchNotifications().catch(err => console.error('Error fetching initial notifications:', err));
	}
	async fetchNotifications() {
		// const notifications = await HTTP.get<Notification[]>('notifications')
		// NotificationService._notifications.set(notifications);
	}

	/**
	 * Drop notifications the backend has just marked as seen, which is what resets the sidebar badge.
	 *
	 * State only - the POST lives in the notification center, because this service is what
	 * `services/http.ts` injects to report request failures and must not import it back.
	 *
	 * Filtered by id instead of cleared wholesale so a notification that arrived over the socket while
	 * the list was open keeps counting: the user has not seen that one.
	 */
	public markSeen(ids: readonly number[]) {
		if (!ids.length) return;
		const seen = new Set(ids);
		NotificationService._notifications.update(notifications => notifications.filter(n => !seen.has(n.id)));
	}

	show(notification: Notification) {
		const container = this.ensureContainer();
		const compRef = createComponent(NotificationComponent as Type<NotificationComponent>, {
			environmentInjector: this.appRef.injector,
			elementInjector: this.injector,
		});
		const {instance, hostView, location} = compRef;
		const key = notification.key;

		instance.detail.set(notification.detail || '');
		instance.summary.set(notification.summary);
		instance.icon.set(notification.icon || '');
		instance.severity.set(notification.severity || 'info');
		instance.sticky.set(!!notification.sticky);
		instance.lifetime.set(notification.lifetime);
		instance.onClick.set(notification.onClick);
		instance.data.set(notification.data);
		instance.closed.subscribe(() => {
			this.appRef.detachView(hostView);
				compRef.destroy();
			this.renderer.removeChild(container, location.nativeElement);
			if (key) {
				const notifications = NotificationService.notificationDict.get(key) ?? [];
				const index = notifications.indexOf(compRef);
				if (index !== -1) {
					notifications.splice(index, 1);
				}
			}
		});

		if (key) {
			if (!NotificationService.notificationDict.has(key)) {
				NotificationService.notificationDict.set(key, []);
			}
			NotificationService.notificationDict.get(key)!.push(compRef);
		}

		this.appRef.attachView(hostView);
		this.renderer.appendChild(container, location.nativeElement);
		return compRef;
	}

	info(notification: Omit<Notification, 'severity'>) {
		return this.show({ ...notification, severity: 'info' });
	}

	success(notification: Omit<Notification, 'severity'>) {
		return this.show({ ...notification, severity: 'success' });
	}

	warn(notification: Omit<Notification, 'severity'>) {
		return this.show({ ...notification, severity: 'warn' });
	}

	error(notification: Omit<Notification, 'severity'>) {
		return this.show({ ...notification, severity: 'error' });
	}

	clear(...keys: string[]) {
		const container = this.ensureContainer();
		keys.forEach(key => {
			const notifications = NotificationService.notificationDict.get(key) ?? [];
			notifications.forEach(compRef => {
				this.appRef.detachView(compRef.hostView);
				compRef.destroy();
				this.renderer.removeChild(container, compRef.location.nativeElement);
			});
			NotificationService.notificationDict.set(key, []);
		});
	}

	private ensureContainer(): HTMLElement {
		if (!NotificationService.CONTAINER) {
			const container = this.renderer.createElement('div');
			this.renderer.addClass(container, 'notification-container');
            this.renderer.appendChild(this.document.body, container);
			NotificationService.CONTAINER = new ElementRef(container);
		}
		return NotificationService.CONTAINER.nativeElement;
	}

	public handleEvent(notification: ServerNotification) {
		// Append first. The list and the sidebar badge are the record of what happened, and must not
		// depend on this client knowing how to toast the type: every branch below used to fall through
		// to a `return` that dropped the row entirely, so eight of the fourteen types the backend emits
		// never reached the badge and only surfaced on the next full page load.
		NotificationService._notifications.update(notifications => [...notifications, notification]);
		this.showToast(notification);
	}

	/**
	 * The link a notification opens, or `null` when it has no target or the route would turn the user
	 * away. Both the notification center rows and the toasts below go through this, so the two cannot
	 * offer different destinations for the same row.
	 */
	public targetFor(notification: {type: string, data: any}): string[] | null {
		const target = notificationTarget(notification.type, notification.data);
		if (!target) return null;
		return this.auth.hasPermission(target.permission) ? target.link : null;
	}

	/**
	 * The i18n params for a notification, with code-valued fields resolved to copy.
	 *
	 * `mail_account_failing.DESCRIPTION` interpolates `{{reason}}`, but the stored payload holds a
	 * stable code. Only the toast used to translate it, so the same notification read as prose in the
	 * popup and as `invalid_credentials` in the notification center.
	 */
	public translationParams(notification: {type: string, data: any}): object {
		const data = notification.data ?? {};
		if (notification.type === 'mail_account_failing') {
			return { ...data, reason: this.translateFailureReason(data) };
		}
		// Either can be missing when the mail could not even be parsed that far; an empty pair of
		// quotes reads like a bug in the copy.
		if (notification.type === 'mail_processing_failed') {
			return { ...data, sender: data.sender || '–', subject: data.subject || '–' };
		}
		// The payload carries Meta's own enum ("RED"), which read as a foreign word dropped into
		// German prose. Resolved here rather than in the copy because a translation key cannot be
		// built from an interpolated value.
		if (notification.type === 'template_quality_update') {
			return { ...data, quality: this.translateQuality(data?.template?.quality_rating) };
		}
		return data;
	}

	/** Meta's rating in the operator's language, falling back to Meta's word for one we lack copy for. */
	private translateQuality(quality: string | undefined): string {
		if (!quality) return '';
		const key = `INTEGRATION.STUDIO_TEMPLATES.QUALITY.${quality}`;
		const translated = this.translate.instant(key);
		return translated === key ? quality : translated;
	}

	/**
	 * Pop a toast for a notification, if this build has anything to say about its type.
	 *
	 * The per-type branches only carry what differs from the default: whether the toast sticks, and
	 * the translated mail reason. Everything else falls through to the generic branch, which is what
	 * lets a type added to the backend show up here as soon as its copy lands in `i18n/`.
	 */
	private showToast(notification: ServerNotification) {
		const { type } = notification;
		const severity = toastSeverity(notification.severity);
		const data = this.translationParams(notification);
		const link = this.targetFor(notification);
		const toast: Notification = {
			summary: `NOTIFICATION.${type}.TITLE`,
			detail: `NOTIFICATION.${type}.DESCRIPTION`,
			data, severity,
			onClick: link ? () => this.router.navigate(link) : undefined,
		};

		switch (type) {
			/**
			 * Losing an account's automatic replies is not something to notice four seconds later. The
			 * severity itself comes from the wire - `routers/branch.py` emits this as a warning, so the
			 * toast and the notification center's icon agree without an override here.
			 */
			case 'message_server_account_reassigned':
			case 'office365_connected':
			/** Mail silently stopped arriving; this must not disappear on its own either. */
			case 'mail_account_failing':
				return void this.show({ ...toast, sticky: true });
			default: {
				// `instant` echoes the key back when it is missing - a type this build has no copy for.
				// A raw translation key in a toast is worse than no toast at all, and the row is in the
				// list and the badge either way.
				if (this.translate.instant(toast.summary) === toast.summary) {
					console.warn('No copy for notification type:', type);
					return;
				}
				this.show({ ...toast, sticky: severity === 'error' });
			}
		}
	}

	/**
	 * Turn a backend failure code into something the studio can read.
	 *
	 * `instant` echoes the key back when it is missing, which is how we detect a reason this client
	 * does not know yet - in that case the backend's raw `detail` is more useful than a stray
	 * translation key on screen.
	 */
	private translateFailureReason(data: {reason: MailAccountFailureReason, detail: string | null}): string {
		const key = `NOTIFICATION.mail_account_failing.REASON.${data.reason}`;
		const translated = this.translate.instant(key);
		if (translated && translated !== key) return translated;
		return data.detail || this.translate.instant('NOTIFICATION.mail_account_failing.REASON.unknown');
	}
}
